import { InboxOutlined } from "@ant-design/icons";
import { Flex, Typography } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { useEffect, useState } from "react";
import { read, utils } from "xlsx";
import AdvancedTable, { EditableColumnType } from "../components/AdvancedTable";
import { DataType } from "../contexts/DataProvider";
import { ResDataDelete } from "../shared/protocols/editor/PtlDataDelete";
import { Filter, SortOrder } from "../shared/protocols/editor/PtlDataFind";
import { ResDataInsert } from "../shared/protocols/editor/PtlDataInsert";
import {
  ResDataUpdate,
  UpdateData,
} from "../shared/protocols/editor/PtlDataUpdate";
import { dataCall } from "../tsrpc";

const { Title } = Typography;

export interface PageMetaDataProps<T extends DataType> {
  title: string;
  collection: string;
  sheetIndex: number;
  header: string[];
  columnStart?: number;
  columnEnd: number;
  sort?: { [key: string]: SortOrder };
  filedSchema?: FieldSchema<T>;
  columns: EditableColumnType<T>[];
}

export function PageMetaData<T extends DataType>({
  title,
  collection,
  sheetIndex,
  header,
  columnStart,
  columnEnd,
  sort,
  filedSchema,
  columns,
}: PageMetaDataProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [version, setVersion] = useState(0);

  const context = {
    data,
    hasMore: false,
    get: async (
      filter?: Filter,
      sort?: { [key: string]: SortOrder },
      limit?: number,
      skip?: number,
      projection?: { [key: string]: 0 | 1 },
      random?: boolean,
    ): Promise<T[]> => [],
    load: async (filter?: Filter, reset?: boolean): Promise<T[]> => {
      const res = await dataCall("editor/DataFind", {
        collection,
        limit: 10000,
        filter,
        sort,
      });
      const data = res.res?.data ?? ([] as T[]);
      setData(data);
      return data;
    },
    update: async (
      filter: Filter,
      update: UpdateData,
      multi?: boolean,
    ): Promise<ResDataUpdate> => ({
      timestamp: 0,
      matchedCount: 0,
      modifiedCount: 0,
    }),
    add: async (
      values: Partial<Omit<T, "_id">> | Partial<Omit<T, "_id">>[],
    ): Promise<ResDataInsert> => {
      await dataCall("editor/DataDelete", { collection, multi: true });
      await dataCall("editor/DataInsert", {
        collection,
        data: values,
      });
      await context.load();
      return {
        timestamp: 0,
        insertedCount: 0,
        insertedIds: [],
      };
    },
    remove: async (ids: T["_id"] | T["_id"][]): Promise<ResDataDelete> => {
      const res = await dataCall("editor/DataDelete", {
        collection,
        filter: { _id: { $in: Array.isArray(ids) ? ids : [ids] } },
        multi: true,
      });
      return {
        timestamp: 0,
        deletedCount: res.res?.deletedCount ?? 0,
      };
    },
  };

  useEffect(() => {
    context.load();
    getVersion();
  }, [collection]);

  const getVersion = async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection },
      limit: 1,
    });
    setVersion(res.res?.data?.[0]?.version ?? 0);
  };

  const parseValue = (value: unknown, type: FieldType) => {
    if (value == null || value === "")
      return type.endsWith("[]") ? [] : undefined;
    switch (type) {
      case "number":
        console.log(value, Number(value));
        return Number(value);
      case "boolean":
        return value === true || value === "true" || value === "1";
      case "string[]":
        return String(value)
          .split(/[,，]/)
          .map((s) => s.trim())
          .filter(Boolean);
      case "number[]":
        return String(value)
          .split(/[,，]/)
          .map((s) => Number(s))
          .filter((v) => !Number.isNaN(v));
      default:
        return value;
    }
  };

  function parseRow<T>(
    row: Record<string, unknown>,
    schema: FieldSchema<T>,
  ): T {
    const result: any = { ...row };
    for (const key in schema) {
      const type = schema[key];
      if (!type) continue;
      result[key] = parseValue(row[key], type);
    }
    return result;
  }

  const onImport = (file: File) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      const wb = read(reader.result, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[sheetIndex]];
      const data = utils.sheet_to_json<T>(sheet, {
        header,
        range: {
          s: { r: 2, c: columnStart || 0 },
          e: { r: 10000, c: columnEnd },
        },
        raw: false,
      });
      const parsed = filedSchema
        ? data.map((row) => parseRow(row, filedSchema))
        : data;
      context.add(parsed);
    };
    return false;
  };

  return (
    <AdvancedTable<T>
      title={() => (
        <Flex justify="space-between" align="center">
          <Flex gap={10}>
            <Title level={5}>{title}</Title>
            <p>{`[数据版本：${version}]`}</p>
          </Flex>
          <Dragger
            accept=".xlsx,.xls"
            itemRender={() => null}
            beforeUpload={onImport}
          >
            <Flex align="center" justify="center" gap={10}>
              <InboxOutlined style={{ fontSize: "20px" }} />
              <span>单击选择或拖动Excel文件到此处导入数据</span>
            </Flex>
          </Dragger>
        </Flex>
      )}
      columns={columns}
      context={context}
    />
  );
}

type FieldType = "string" | "number" | "boolean" | "string[]" | "number[]";
type FieldSchema<T> = { [K in keyof T]?: FieldType };
